import * as React from 'react';
import styled, { useTheme } from 'styled-components/macro';
import { useState } from 'react';
import { GroupListItem, ContactListItem } from 'lib/api/types';
import { exportCSVFile } from 'lib/utils/functions';
import { CheckboxInput, SelectionHeaderPopup } from 'lib/components';
import { Button } from 'react-covideo-common';
import { MdDeleteForever, MdAddToPhotos, MdFileDownload } from 'react-icons/md';
import { ModalMergeGroup } from 'lib/components/modal/ModalMergeGroup';
import { useMergeGroupsMutation } from 'lib/api/group/useMergeGroupsMutation';
import { QueryParams } from 'lib/api/group/useQueryGroups';
import { useTranslation } from 'react-i18next';
import { useCDK } from 'lib/hooks/useCdk';
import { ModalDeleteGroup } from '../../details/components/ModalDeleteGroup';

type SelectedGroups = {
  count: number;
  groups: Map<string, GroupListItem | undefined>;
};

interface ComponentProps {
  selectedGroups: SelectedGroups;
  setSelectedGroups: React.Dispatch<React.SetStateAction<SelectedGroups>>;
  onDelete: Function;
  checkIndicator: Function;
  isAllSelected: boolean;
  selectAll: Function;
  isVisible?: boolean;
  queryParams: QueryParams;
}

const LayoutOption = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 12px;
`;
const SelectionCountWrap = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  padding-right: 15px;
  font-size: 14px;
  color: ${({ theme }) => theme.colors.black[100]};
`;

const SelectionCountText = styled.div`
  margin: 0 8px 0 16px;
  font-size: 14px;
`;

const Component = (props: ComponentProps) => {
  const {
    selectedGroups,
    setSelectedGroups,
    onDelete,
    checkIndicator,
    isAllSelected,
    selectAll,
    isVisible = false,
    queryParams,
  } = props;

  const { t } = useTranslation();
  const { destructiveToText } = useCDK();
  const { colors } = useTheme();
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [isModalMergeGroupsVisible, setIsModalMergeGroupsVisible] =
    useState(false);

  const exportHeader = {
    email: t('Email'),
    lastName: t('Last Name'),
    firstName: t('First Name'),
    phone: t('Phone'),
  };

  const ids: number[] = Array.from(selectedGroups.groups)
    .filter(([, value]) => value !== undefined)
    .map(([key]) => parseInt(key));

  const onMergeGroupsMutationSuccess = () => {
    onDelete();
    setIsModalMergeGroupsVisible(false);
  };

  const { mutateAsync: mergeGroupsMutation } = useMergeGroupsMutation(
    onMergeGroupsMutationSuccess,
    queryParams
  );

  const onMergeGroup = async (data: { name: string }) => {
    await mergeGroupsMutation({ ids: ids, name: data.name });
  };

  const getContactsFromGroups = (groups: (GroupListItem | undefined)[]) => {
    let result: ContactListItem[] = [];
    groups.forEach(a => {
      if (a && a.contacts) result = result.concat(a.contacts);
    });
    return result;
  };

  return (
    <>
      {(selectedGroups.count > 0 || isVisible) && (
        <SelectionHeaderPopup left='208px' fixedWidth='1032px'>
          <LayoutOption>
            <CheckboxInput
              blueCheck={true}
              checkGroupIndicator={checkIndicator()}
              checked={isAllSelected}
              onClick={(event: React.ChangeEvent<HTMLInputElement>) => {
                event.stopPropagation();
              }}
              onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                selectAll({ event })
              }
            />
            <SelectionCountWrap>
              <SelectionCountText>{t('Groups selected:')}</SelectionCountText>
              <b>{selectedGroups.count}</b>
            </SelectionCountWrap>
            <Button
              onClick={() => setIsModalMergeGroupsVisible(true)}
              text={t('Merge Groups')}
              variant='ghost'
              disabled={selectedGroups.count < 2}
              icon={<MdAddToPhotos />}
            ></Button>
            <Button
              onClick={() =>
                exportCSVFile(
                  exportHeader,
                  getContactsFromGroups(
                    Array.from(selectedGroups.groups.values())
                  ),
                  'selected_contacts'
                )
              }
              text={t('Export')}
              variant='ghost'
              icon={<MdFileDownload />}
            ></Button>
            <Button
              onClick={() => setShowDeleteModal(true)}
              text={t('Delete')}
              variant={destructiveToText}
              icon={<MdDeleteForever color={colors.danger[100]} width={20} />}
            ></Button>
          </LayoutOption>
        </SelectionHeaderPopup>
      )}
      {showDeleteModal && (
        <ModalDeleteGroup
          onModalClose={() => {
            // remove selection
            setSelectedGroups({ count: 0, groups: new Map() });
            setShowDeleteModal(false);
          }}
          groupIds={ids}
        />
      )}
      {isModalMergeGroupsVisible && (
        <ModalMergeGroup
          title={t('Merge Groups')}
          text=''
          handleModalClose={() => setIsModalMergeGroupsVisible(false)}
          handleSubmit={onMergeGroup}
        />
      )}
    </>
  );
};

export const GroupsHeader = React.memo(Component);
